/* Copyright (c) 2020-present CloudIO Inc */

import { IoPages, Row, toNewRow } from '@cloudio-saas/datasource-types';
import { useStateXAtomSetter, useStateXResetter } from '@cloudio-saas/statex';
import {
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  closeSidebarOnPageNavigationAtom,
  useDeveloperMode,
} from 'designer/atoms';
import Icon from 'icons/Icon';
import {
  openViewDatasourceListAtom,
  viewDatasourcesUsedInfoAtom,
} from 'layout/Header/HeaderState';
import {
  activeSidebarPanelAtom,
  PageView,
  sidebarActivePageIdAtom,
  sidebarPageWizardTitleAtom,
} from 'layout/Sidebar/atoms';
import { usePending } from 'platform/PlatformHooks';
import { PlatformContext } from 'platform/PlatformProvider';
import React, { memo, useContext } from 'react';
import useUser, {
  isPatchAdministrator,
  isViewOnlyDeveloper,
} from 'root/useUser';
import Store from 'store/Store';
import { useIoPagesCopyStore, useIoPagesEditStore } from 'store/utils/utils';
import { IconPulse } from 'utils/Pulse';
import WaveDots from 'utils/WaveDots';
import { selectionAtom } from './PagesHistory';
import useDataStore from 'store/useDataStore';
import { AnyItem, getManager } from 'componentregistry';
import { isOfTypeDataStore } from 'layout/Header/DatasourcesUsed';
import { DataStoreItem } from 'componentregistry/components/DataStore/DataStore';
import { getUrl } from 'config/config';
import { getURLQueryStringToPost } from 'utils/url';
import FileSaver from 'file-saver';
import { ActionInvokeCloudFunctionItem } from 'componentregistry/actions/ActionInvokeCloudFunction/ActionInvokeCloudFunction';
import { isEmpty } from 'utils/isEmpty';

const useStyles = makeStyles(() => ({
  listIcon: {
    minWidth: 32,
  },
}));

function PageActions({
  className,
  value,
  setView,
  store,
  record,
  appUid,
}: {
  appUid: string;
  className: string;
  value: string;
  setView: (_view: PageView) => void;
  store: Store<IoPages>;
  record: Row<IoPages>;
}): React.ReactElement {
  const classes = useStyles();
  const { userName } = useUser();
  const platform = useContext(PlatformContext);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | undefined>(
    undefined,
  );
  const open = Boolean(anchorEl);
  const [isPending1, setPending1] = usePending();
  const [isPending2, setPending2] = usePending();
  const [isPending3, setPending3] = usePending();
  const [isPending4, setPending4] = usePending();
  const [isPending5, setPending5] = usePending();
  const [isPending6, setPending6] = usePending();
  const user = useUser();
  const setSidebarPanel = useStateXAtomSetter(activeSidebarPanelAtom);
  const reset = useStateXResetter();
  const pageEditstore = useIoPagesEditStore(appUid);
  const pageCopyStore = useIoPagesCopyStore(appUid);
  const setTitle = useStateXAtomSetter(sidebarPageWizardTitleAtom);
  const setActivePageId = useStateXAtomSetter(sidebarActivePageIdAtom);
  const devMode = useDeveloperMode(appUid, record.pageUid ?? 'x');
  const openDSList = useStateXAtomSetter(openViewDatasourceListAtom);
  const setViewDatasourcesUsedInfo = useStateXAtomSetter(
    viewDatasourcesUsedInfoAtom,
  );
  const isViewOnlyUser = isViewOnlyDeveloper(platform, appUid);
  const patchAdmin = isPatchAdministrator(platform, appUid);
  const pageRefStore = useDataStore<IoPages>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoPages',
    alias: 'IoPagesDSUsedAlias',
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    store.setCurrentRecordId(value);
    const r = store.getRecord(value);
    if (r?.pageUid) {
      setActivePageId(r.pageUid);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleClose = (event?: any) => {
    event?.stopPropagation();
    setAnchorEl(undefined);
  };

  async function downloadPagePatch() {
    await pageRefStore.query({
      props: {
        projection: {
          pageName: 1,
          md: 1,
        },
        filter: [
          { appUid: { is: appUid } },
          { pageUid: { is: record?.pageUid || '' } },
        ],
      },
    });

    const pageRecord = pageRefStore.getCurrentRecord();
    if (pageRecord) {
      const { md } = pageRecord;
      if (md) {
        const ids = md ? Object.keys(md) : [];
        const dsList: string[] = [];
        const fnList: string[] = [];
        ids.forEach((id) => {
          const item = md[id] as AnyItem;
          const mgr = getManager(item.type);
          if (isOfTypeDataStore(mgr.type)) {
            const dsItem = item as DataStoreItem;
            if (dsList.indexOf(dsItem.props.dataSource) === -1) {
              dsList.push(dsItem.props.dataSource);
            }
          } else if (mgr.type === 'ActionInvokeCloudFunction') {
            const invokeFnItem = item as ActionInvokeCloudFunctionItem;
            const fnUri = invokeFnItem.props.functionUri;
            if (!isEmpty(fnUri) && fnList.indexOf(fnUri) === -1) {
              fnList.push(fnUri);
            }
          }
        });

        try {
          const response = await fetch(
            `${getUrl()}download_patch_for_ids?${getURLQueryStringToPost(
              user,
            )}`,
            {
              method: 'POST',
              credentials: 'include',
              headers: {
                'X-Application': appUid,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                pages: [record.pageUid],
                dataSources: dsList,
                functions: fnList,
              }),
            },
          );
          if (response.status === 200) {
            const blob = await response.blob();
            // replace spaces with empty string in pageName
            FileSaver.saveAs(
              blob,
              `${record.pageName?.replace(/\s/g, '')}.zip`,
            );
          } else {
            const json = await response.json();
            platform.showError(`Error downloading patch. ${json.message}`);
          }
        } finally {
          setPending6(false);
        }
      }
    } else {
      platform.showError(
        `Error downloading patch. Could not find page ${record.pageName}`,
      );
      setPending6(false);
    }
  }

  return (
    <>
      <IconButton
        data-testid={`${record.pageUid}-nav-page`}
        className={className}
        data-tip={`Navigate to ${record.pageName} Page`}
        aria-label={`Navigate to ${record.pageName} Page`}
        onClick={async (e) => {
          e.stopPropagation();
          store.setCurrentRecordId(value);
          platform.goToPage(appUid, record.pageUid);
          if (!devMode) {
            const close = platform.getter(closeSidebarOnPageNavigationAtom, {
              params: { userName },
            });
            if (close) {
              setSidebarPanel('HIDDEN');
            }
          }
        }}
      >
        <span aria-hidden="false" style={{ display: 'none' }}>
          {`Navigate to ${record.pageName} Page`}
        </span>
        <Icon iconName="eye" />
      </IconButton>
      <IconButton
        data-testid={`${record.pageUid}-edit-page`}
        className={className}
        color="primary"
        aria-label={`Edit ${record.pageName} Page`}
        data-tip={`Edit ${record.pageName} Page`}
        onClick={(e) => {
          platform.startAsyncTransition(async () => {
            e.stopPropagation();
            store.setCurrentRecordId(value);
            const r = store.getRecord(value);
            if (r?.pageUid) {
              setActivePageId(r.pageUid);
              setTitle('Edit Page');
              setView('edit');
            }
          }, setPending1);
        }}
      >
        <span aria-hidden="false" style={{ display: 'none' }}>
          {`Edit ${record.pageName} Page`}
        </span>
        {isPending1 ? <WaveDots active /> : <Icon iconName="edit" />}
      </IconButton>

      <IconButton
        data-testid={`${record.pageUid}-more-page`}
        className={className}
        color="primary"
        aria-label="display more actions"
        onClick={handleClick}
      >
        <span aria-hidden="false" style={{ display: 'none' }}>
          More Actions
        </span>
        <Icon iconName="ellipsis-v" />
        <IconPulse
          pulseKey="pg_more"
          pulseMessage="Click this icon to view more actions, that you can perform on this page."
        />
      </IconButton>
      {open && anchorEl ? (
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          keepMounted
          open={open}
          onClose={handleClose}
        >
          <MenuItem
            data-testid="nav-page-nocc"
            onClick={(e) => {
              e.stopPropagation();
              store.setCurrentRecordId(value);
              platform.goToPage(appUid, record.pageUid, 'skipCustomCode=Y');
              if (!devMode) {
                const close = platform.getter(
                  closeSidebarOnPageNavigationAtom,
                  {
                    params: { userName },
                  },
                );
                if (close) {
                  setSidebarPanel('HIDDEN');
                }
              }
            }}
          >
            <ListItemIcon className={classes.listIcon}>
              {isPending2 ? <WaveDots active /> : <Icon iconName="eye-slash" />}
            </ListItemIcon>
            Navigate with no Controller Code
          </MenuItem>
          <MenuItem
            data-testid="access"
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
              platform.startAsyncTransition(async () => {
                setView('access');
              }, setPending2);
            }}
          >
            <ListItemIcon className={classes.listIcon}>
              {isPending2 ? (
                <WaveDots active />
              ) : (
                <Icon iconName="shield-alt" />
              )}
            </ListItemIcon>
            Access Settings
          </MenuItem>
          <MenuItem
            data-testid="duplicate"
            disabled={isViewOnlyUser}
            onClick={async (e) => {
              e.stopPropagation();
              handleClose();
              platform.startAsyncTransition(async () => {
                store.setCurrentRecordId(value);
                await pageCopyStore
                  .query({
                    props: {
                      filter: [{ pageUid: { is: record.pageUid } }],
                      limit: 1,
                    },
                  })
                  .then(async () => {
                    const row = pageCopyStore.getRecord(value);
                    await pageEditstore.clear();
                    await pageEditstore.createNew({
                      partialRecord: {
                        ...toNewRow(row),
                        pageName: `Copy Of ${row.pageName}`,
                        pageUid: `copy-of-${record.pageUid}`,
                      },
                      rs: 'I',
                    });
                    await pageCopyStore.clear();
                    setTitle(`Duplicate Page '${row.pageName}'`);
                  });
                setActivePageId('');
                await store.createNew({ partialRecord: {}, rs: 'N' });
                setView('edit');
              }, setPending3);
            }}
          >
            <ListItemIcon className={classes.listIcon}>
              {isPending3 ? <WaveDots active /> : <Icon iconName="copy" />}
            </ListItemIcon>
            Duplicate
          </MenuItem>
          <MenuItem
            data-testid="change-history"
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
              platform.startAsyncTransition(async () => {
                store.setCurrentRecordId(value);
                const r = store.getRecord(value);
                if (r.pageUid) {
                  setActivePageId(r.pageUid);
                  reset(selectionAtom);
                  setView('history');
                }
              }, setPending5);
            }}
          >
            <ListItemIcon className={classes.listIcon}>
              {isPending5 ? <WaveDots active /> : <Icon iconName="history" />}
            </ListItemIcon>
            Change History
          </MenuItem>
          <MenuItem
            data-testid="referred-datasources"
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
              setViewDatasourcesUsedInfo({
                pageId: record?.pageUid ?? '',
                pageName: record?.pageName,
              });
              openDSList(true);
            }}
          >
            <ListItemIcon className={classes.listIcon}>
              <Icon iconName="coins" />
            </ListItemIcon>
            Referred Datasources
          </MenuItem>

          {patchAdmin ? (
            <MenuItem
              data-testid="download-patch"
              onClick={async (e) => {
                e.stopPropagation();
                setPending6(true);
                await downloadPagePatch();
              }}
            >
              <ListItemIcon className={classes.listIcon}>
                {isPending6 ? (
                  <WaveDots active />
                ) : (
                  <Icon iconName="download" disabled={isPending6} />
                )}
              </ListItemIcon>
              Download Patch
            </MenuItem>
          ) : null}

          <Divider />
          <MenuItem
            data-testid="delete"
            disabled={isViewOnlyUser}
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
              store.setCurrentRecordId(value);
              platform.confirmDialog({
                title: 'Delete Page',
                content: `Are you sure you want to delete this Page ${record.pageName} [${record.pageUid}]?`,
                onOk: async () => {
                  platform.startAsyncTransition(async () => {
                    await store.deleteRecord(value);
                    const r = await store.save({
                      feedback: `Page '${record.pageName} [${record.pageUid}]' deleted successfully!`,
                    });
                    if (!r) {
                      // delete failed. reset the store, or else this delete will always
                      // be sent back to the server, even when trying to update other things
                      // on this same store
                      store.reset();
                    } else if (platform.pageId === record.pageUid) {
                      platform.goToPage(appUid);
                      platform.resource().clearPage(appUid, record.pageUid);
                      platform.remover(['db', 'IoPages', record.pageUid]);
                    }
                  }, setPending4);
                },
              });
            }}
          >
            <ListItemIcon className={classes.listIcon}>
              {isPending4 ? (
                <WaveDots active />
              ) : (
                <Icon iconName="trash" color="error" />
              )}
            </ListItemIcon>
            Delete
          </MenuItem>
        </Menu>
      ) : null}
    </>
  );
}

export default memo(PageActions);
